import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/contexts/ProjectContext";
import {
  LayoutDashboard,
  Wallet,
  FolderOpen,
  KanbanSquare,
  Settings,
  LogOut,
  Briefcase,
  Users,
  Check,
  ChevronsUpDown,
  Plus,
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import ProjectFormDialog from "@/components/ProjectFormDialog";

const navLinks = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: null, testid: "nav-dashboard" },
  { to: "/accounts", label: "Accounts", icon: Wallet, perm: "accounts", testid: "nav-accounts" },
  { to: "/resources", label: "Resources", icon: FolderOpen, perm: "resources", testid: "nav-resources" },
  { to: "/tasks", label: "Tasks", icon: KanbanSquare, perm: "tasks", testid: "nav-tasks" },
  { to: "/settings", label: "Project Settings", icon: Settings, perm: "settings", testid: "nav-settings" },
];

function ProjectSwitcher() {
  const { projects, selected, selectProject, loadProjects } = useProject();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild data-testid="project-switcher-trigger">
          <button className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-indigo-300 transition-colors min-w-[220px] text-left">
            <div className="h-8 w-8 rounded-md bg-indigo-50 grid place-items-center text-indigo-700 flex-shrink-0">
              <Briefcase className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500">Current Project</div>
              <div className="text-sm font-medium text-slate-900 truncate">
                {selected?.name || "No project selected"}
              </div>
            </div>
            <ChevronsUpDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel>Switch Project</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {projects.length === 0 && (
            <div className="px-2 py-3 text-sm text-slate-500">No projects yet.</div>
          )}
          {projects.map((p) => (
            <DropdownMenuItem
              key={p.id}
              onClick={() => selectProject(p.id)}
              data-testid={`project-option-${p.id}`}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm">{p.name}</span>
                <Badge variant="secondary" className="ml-auto text-[10px]">{p.status}</Badge>
                {selected?.id === p.id && <Check className="h-4 w-4 text-indigo-600 ml-1" />}
              </div>
            </DropdownMenuItem>
          ))}
          {user?.role === "owner" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { setOpen(false); setCreating(true); }} className="cursor-pointer text-indigo-700" data-testid="create-project-menu-item">
                <Plus className="h-4 w-4 mr-2" /> New Project
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ProjectFormDialog
        open={creating}
        onOpenChange={setCreating}
        onSaved={async (p) => {
          await loadProjects();
          selectProject(p.id);
        }}
      />
    </>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "U").substring(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
        <button className="flex items-center gap-2 rounded-lg hover:bg-slate-100 px-2 py-1.5 transition-colors">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="hidden md:block text-left">
            <div className="text-sm font-medium text-slate-900">{user?.name}</div>
            <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="font-medium">{user?.name}</div>
          <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user?.role === "owner" && (
          <DropdownMenuItem onClick={() => navigate("/team")} className="cursor-pointer" data-testid="menu-team">
            <Users className="h-4 w-4 mr-2" /> Team Members
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/change-password")} className="cursor-pointer" data-testid="menu-change-password">
          <Settings className="h-4 w-4 mr-2" /> Change Password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="cursor-pointer text-rose-600" data-testid="menu-logout">
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user } = useAuth();
  const { myPermissions, selected } = useProject();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col h-screen fixed left-0 top-0 z-40">
        <div className="px-6 h-16 flex items-center gap-3 border-b border-slate-100">
          <div className="h-9 w-9 rounded-lg bg-indigo-600 grid place-items-center text-white">
            <Briefcase className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-semibold text-slate-900 leading-tight">Ecom PM</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Project Hub</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navLinks.map((l) => {
            const allowed = user?.role === "owner" || !l.perm || myPermissions.includes(l.perm);
            if (!allowed) return null;
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-indigo-50 text-indigo-700 font-medium"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {l.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-3 border-t border-slate-100">
          <div className="text-xs text-slate-500 px-3">
            {selected ? (
              <>
                <div className="font-medium text-slate-700 truncate">{selected.client_name}</div>
                <div className="truncate">{selected.client_contact}</div>
              </>
            ) : (
              "Select a project"
            )}
          </div>
        </div>
      </aside>

      {/* Topbar */}
      <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6 lg:ml-64">
        <ProjectSwitcher />
        <UserMenu />
      </header>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 flex justify-around py-2">
        {navLinks.map((l) => {
          const allowed = user?.role === "owner" || !l.perm || myPermissions.includes(l.perm);
          if (!allowed) return null;
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-${l.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-2 py-1 text-[10px] ${
                  isActive ? "text-indigo-700" : "text-slate-500"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {l.label.split(" ")[0]}
            </NavLink>
          );
        })}
      </nav>

      <main className="lg:ml-64 px-4 lg:px-8 py-6 pb-24 lg:pb-12">{children}</main>
    </div>
  );
}
